import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, FileText, Users, Clock, RefreshCw, ArrowRight, ChevronLeft, Trash2, CalendarPlus, CalendarX } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

export default function CaseDetails() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calendarLoading, setCalendarLoading] = useState(false);

  useEffect(() => {
    fetchCase();
  }, [caseId]);

  const fetchCase = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(response.data);
    } catch (error) {
      toast.error('فشل تحميل بيانات القضية');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      const res = await axios.post(`${API}/cases/${caseId}/refresh`);
      if (res.data.status === 'updated') {
        toast.success(`تم التحديث - ${res.data.changes} تغييرات`);
        fetchCase();
      } else if (res.data.status === 'scraping_unavailable') {
        toast.warning('خدمة الجلب التلقائي غير متوفرة حالياً. يمكنك تحديث البيانات يدوياً.');
      } else {
        toast.info('لا توجد تغييرات جديدة');
      }
    } catch (error) {
      toast.error('فشل التحديث');
    }
  };

  const handleAddToCalendar = async () => {
    try {
      setCalendarLoading(true);
      const res = await axios.post(`${API}/cases/${caseId}/calendar`);
      toast.success(res.data.message || 'تمت إضافة الجلسة إلى التقويم');
      fetchCase();
    } catch (error) {
      const message = error.response?.data?.detail || 'فشل إضافة الجلسة للتقويم';
      toast.error(message);
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleRemoveFromCalendar = async () => {
    try {
      setCalendarLoading(true);
      const res = await axios.delete(`${API}/cases/${caseId}/calendar`);
      toast.success(res.data.message || 'تم حذف الحدث من التقويم');
      fetchCase();
    } catch (error) {
      const message = error.response?.data?.detail || 'فشل حذف الحدث من التقويم';
      toast.error(message);
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('هل أنت متأكد من حذف هذه القضية؟ لا يمكن التراجع عن هذا الإجراء.')) {
      return;
    }

    try {
      await axios.delete(`${API}/cases/${caseId}`);
      toast.success('تم حذف القضية بنجاح');
      navigate('/');
    } catch (error) {
      toast.error('فشل حذف القضية');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'منظورة': 'bg-blue-100 text-blue-700',
      'مؤجلة': 'bg-yellow-100 text-yellow-700',
      'محجوزة للحكم': 'bg-purple-100 text-purple-700',
      'شطب': 'bg-gray-100 text-gray-700',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!caseData) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="hover:bg-gray-100"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              القضية رقم: {caseData.case_number || caseData.auto_number}
            </h1>
            <p className="text-gray-500 mt-1">الرقم الآلي: {caseData.auto_number}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate(`/cases/${caseId}/edit`)} 
            variant="outline"
            className="gap-2"
            data-testid="edit-case-btn"
          >
            <FileText className="w-4 h-4" />
            تعديل القضية
          </Button>
          <Button data-testid="refresh-case-btn" onClick={handleRefresh} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <RefreshCw className="w-4 h-4" />
            تحديث البيانات
          </Button>
          <Button 
            data-testid="delete-case-btn" 
            onClick={handleDelete} 
            variant="destructive"
            className="gap-2"
          >
            <Trash2 className="w-4 h-4" />
            حذف القضية
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="case-status-card">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">الحالة</p>
              <Badge className={`${getStatusBadge(caseData.status)} text-base px-4 py-1`}>
                {caseData.status || 'غير محدد'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="case-court-card">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">المحكمة</p>
              <p className="font-semibold text-gray-900">{caseData.court || 'غير متوفر'}</p>
              {caseData.department && (
                <p className="text-sm text-gray-600 mt-1">{caseData.department}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="case-type-card">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-2">نوع القضية</p>
              <p className="font-semibold text-gray-900">{caseData.case_type || 'غير متوفر'}</p>
              {caseData.year && (
                <p className="text-sm text-gray-600 mt-1">السنة: {caseData.year}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview">نظرة عامة</TabsTrigger>
          <TabsTrigger value="hearings" data-testid="tab-hearings">الجلسات</TabsTrigger>
          <TabsTrigger value="parties" data-testid="tab-parties">الأطراف</TabsTrigger>
          <TabsTrigger value="changelog" data-testid="tab-changelog">سجل التغييرات</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>معلومات عامة</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {caseData.next_hearing && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-6 h-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-blue-900">الجلسة القادمة</h4>
                      <p className="text-blue-700 mt-1">
                        {new Date(caseData.next_hearing.date).toLocaleDateString('ar-KW', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {caseData.next_hearing.notes && (
                        <p className="text-sm text-blue-600 mt-2">{caseData.next_hearing.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-4">
                  <p className="text-sm text-gray-500">تاريخ الإضافة</p>
                  <p className="font-semibold mt-1">
                    {new Date(caseData.created_at).toLocaleDateString('ar-KW')}
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <p className="text-sm text-gray-500">آخر تحديث</p>
                  <p className="font-semibold mt-1">
                    {new Date(caseData.last_updated).toLocaleDateString('ar-KW')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hearings">
          <Card>
            <CardHeader>
              <CardTitle>سجل الجلسات</CardTitle>
            </CardHeader>
            <CardContent>
              {caseData.hearings && caseData.hearings.length > 0 ? (
                <div className="space-y-4">
                  {caseData.hearings.map((hearing, index) => (
                    <div
                      key={index}
                      data-testid={`hearing-${index}`}
                      className="border-r-4 border-blue-500 bg-gray-50 rounded-lg p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 text-gray-600 mt-1" />
                          <div>
                            <p className="font-semibold text-gray-900">
                              {new Date(hearing.date).toLocaleDateString('ar-KW', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            {hearing.decision && (
                              <p className="text-gray-700 mt-1">القرار: {hearing.decision}</p>
                            )}
                            {hearing.notes && (
                              <p className="text-sm text-gray-600 mt-2">{hearing.notes}</p>
                            )}
                          </div>
                        </div>
                        <Badge variant="outline">الجلسة {caseData.hearings.length - index}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  لا توجد جلسات مسجلة
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="parties">
          <Card>
            <CardHeader>
              <CardTitle>أطراف القضية</CardTitle>
            </CardHeader>
            <CardContent>
              {caseData.parties && caseData.parties.length > 0 ? (
                <div className="space-y-3">
                  {caseData.parties.map((party, index) => (
                    <div
                      key={index}
                      data-testid={`party-${index}`}
                      className="flex items-center gap-4 border rounded-lg p-4 bg-gray-50"
                    >
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{party.name}</p>
                        <p className="text-sm text-gray-600">{party.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  لا توجد بيانات عن الأطراف
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="changelog">
          <Card>
            <CardHeader>
              <CardTitle>سجل التغييرات</CardTitle>
            </CardHeader>
            <CardContent>
              {caseData.change_log && caseData.change_log.length > 0 ? (
                <div className="space-y-3">
                  {caseData.change_log.map((change, index) => (
                    <div
                      key={index}
                      data-testid={`change-${index}`}
                      className="border rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex items-start gap-3">
                        <ArrowRight className="w-5 h-5 text-blue-600 mt-1" />
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{change.field}</p>
                          <div className="flex items-center gap-2 mt-2 text-sm">
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                              {typeof change.old_value === 'object' ? JSON.stringify(change.old_value) : change.old_value || 'فارغ'}
                            </span>
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                              {typeof change.new_value === 'object' ? JSON.stringify(change.new_value) : change.new_value}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(change.timestamp).toLocaleString('ar-KW')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  لا توجد تغييرات مسجلة
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}